import React from 'react';
import styled from '@emotion/styled';
import Layout from '../components/layout/Layout';
import { theme } from '../styles/theme';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${theme.spacing['2xl']} ${theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['3xl']};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.xl};
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing['2xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSize['xl']};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.lg};
`;

const Text = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.md};
`;

const List = styled.ul`
  margin-bottom: ${theme.spacing.lg};
  padding-left: ${theme.spacing.xl};
  
  li {
    color: ${theme.colors.text.secondary};
    margin-bottom: ${theme.spacing.sm};
    line-height: 1.6;
  }
`;

const HighlightBox = styled.div`
  background: ${theme.colors.background.secondary || '#f8f9fa'};
  border-left: 4px solid ${theme.colors.primary.main};
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.lg} 0;
  border-radius: 4px;
`;

const LastUpdated = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.light};
  margin-top: ${theme.spacing['2xl']};
  text-align: center;
`;

const CookiePolicy = () => {
  return (
    <Layout>
      <Container>
        <Title>Cookie Policy</Title>

        <Section>
          <Text>
            At Neat Schedule, we use cookies and similar technologies to enhance your experience on our website 
            and provide essential functionality. This Cookie Policy explains what cookies we use, why we use them, 
            and how you can manage your cookie preferences.
          </Text>
        </Section>

        <Section>
          <SectionTitle>What Are Cookies?</SectionTitle>
          <Text>
            Cookies are small text files that are stored on your device when you visit our website. They help us 
            remember your preferences and provide you with a better, more personalized experience. Cookies are widely 
            used by websites to make them work more efficiently and to provide information to website owners.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Types of Cookies We Use</SectionTitle>
          
          <Section>
            <SectionTitle>Essential Cookies (Required)</SectionTitle>
            <HighlightBox>
              <Text>
                <strong>These cookies are necessary for our website to function properly and cannot be disabled.</strong>
              </Text>
            </HighlightBox>
            <Text>We use essential cookies to:</Text>
            <List>
              <li>Maintain your login session and authentication status</li>
              <li>Remember your account preferences and settings</li>
              <li>Process your appointments and scheduling requests</li>
              <li>Secure your personal information and prevent unauthorized access</li>
              <li>Enable core website functionality like navigation and form submissions</li>
              <li>Remember items in your shopping cart during checkout</li>
              <li>Store your contact information for appointment booking</li>
            </List>
            <Text>
              Without these cookies, you would not be able to use our scheduling services, create appointments, 
              or access your account securely. These cookies do not collect personal information that could be 
              used to identify you outside of our service.
            </Text>
          </Section>

          <Section>
            <SectionTitle>Functional Cookies</SectionTitle>
            <Text>These cookies enhance your experience by:</Text>
            <List>
              <li>Remembering your language and region preferences</li>
              <li>Storing your display preferences (theme, layout)</li>
              <li>Remembering your notification settings</li>
              <li>Enabling accessibility features you've selected</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>Analytics Cookies</SectionTitle>
            <Text>We use analytics cookies to:</Text>
            <List>
              <li>Understand how visitors use our website</li>
              <li>Identify which features are most popular</li>
              <li>Improve our website performance and user experience</li>
              <li>Monitor website security and detect potential issues</li>
            </List>
            <Text>
              These cookies collect anonymous information and help us make our website better for all users.
            </Text>
          </Section>
        </Section>

        <Section>
          <SectionTitle>How We Use Your Information</SectionTitle>
          <Text>The information collected through cookies is used to:</Text>
          <List>
            <li>Provide and maintain our scheduling services</li>
            <li>Process your appointments and manage your bookings</li>
            <li>Send you appointment reminders and updates</li>
            <li>Improve our website functionality and user experience</li>
            <li>Ensure the security of your account and personal information</li>
            <li>Comply with legal and regulatory requirements</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>Managing Your Cookie Preferences</SectionTitle>
          <Text>
            You can control and manage cookies in several ways. Please note that disabling certain cookies may 
            affect the functionality of our website and your ability to use our services.
          </Text>
          
          <Section>
            <SectionTitle>Browser Settings</SectionTitle>
            <Text>
              Most web browsers allow you to control cookies through their settings. You can usually find these 
              settings in the "Options" or "Preferences" menu of your browser. You can choose to:
            </Text>
            <List>
              <li>Block all cookies</li>
              <li>Block third-party cookies only</li>
              <li>Delete existing cookies</li>
              <li>Receive notifications when cookies are set</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>Cookie Consent</SectionTitle>
            <Text>
              When you first visit our website, you may see a cookie consent banner. You can choose to accept 
              all cookies, reject non-essential cookies, or customize your preferences. Essential cookies 
              cannot be disabled as they are required for our website to function.
            </Text>
          </Section>
        </Section>

        <Section>
          <SectionTitle>Third-Party Cookies</SectionTitle>
          <Text>
            We may use third-party services that set their own cookies. These include:
          </Text>
          <List>
            <li><strong>Payment Processors:</strong> Cookies from payment providers to process your payments securely</li>
            <li><strong>Analytics Services:</strong> Cookies from Google Analytics or similar services to help us understand website usage</li>
            <li><strong>Communication Services:</strong> Cookies from email and SMS providers to send you appointment reminders</li>
            <li><strong>Security Services:</strong> Cookies from security providers to protect against fraud and unauthorized access</li>
          </List>
          <Text>
            These third-party cookies are subject to the respective privacy policies of those services.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Data Security</SectionTitle>
          <Text>
            We take the security of your information seriously. All cookies are transmitted securely, and we 
            implement appropriate technical and organizational measures to protect your personal information. 
            However, please note that no method of transmission over the internet is 100% secure.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Updates to This Policy</SectionTitle>
          <Text>
            We may update this Cookie Policy from time to time to reflect changes in our practices or for other 
            operational, legal, or regulatory reasons. We will notify you of any material changes by posting 
            the updated policy on our website and updating the "Last Updated" date.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Contact Us</SectionTitle>
          <Text>
            If you have any questions about this Cookie Policy or our use of cookies, please contact us at{' '}
            <a 
              href="mailto:privacy@neatschedule.com" 
              style={{ color: theme.colors.primary.main }}
            >
              privacy@neatschedule.com
            </a>
          </Text>
        </Section>

        <LastUpdated>
          Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </LastUpdated>
      </Container>
    </Layout>
  );
};

export default CookiePolicy;
